/* personalize-popup-close.js
 * Standalone helper for PDP:
 * Closes Zepto personalization popup after successful add-to-cart,
 * once the theme shows cart feedback (quick cart / confirmation).
 * Shows a full-screen "Adding to cart" overlay with loader while this happens.
 */
(function () {
  'use strict';

  /* ─── Adding-to-cart overlay ─────────────────────────────────────────── */

  var OVERLAY_ID = '__pdp_adding_overlay';

  function showAddingOverlay() {
    if (document.getElementById(OVERLAY_ID)) return;

    var style = document.createElement('style');
    style.id = OVERLAY_ID + '_style';
    style.textContent = [
      '#' + OVERLAY_ID + '{',
      '  position:fixed;inset:0;z-index:2147483647;',
      '  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;',
      '  background:rgba(0,0,0,0.55);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);',
      '  opacity:0;transition:opacity 0.25s ease;',
      '}',
      '#' + OVERLAY_ID + '.visible{opacity:1;}',
      '#' + OVERLAY_ID + '__spinner{',
      '  width:52px;height:52px;border-radius:50%;',
      '  border:4px solid rgba(255,255,255,0.25);',
      '  border-top-color:#fff;',
      '  animation:__pdp_spin 0.75s linear infinite;',
      '}',
      '@keyframes __pdp_spin{to{transform:rotate(360deg)}}',
      '#' + OVERLAY_ID + '__label{',
      '  color:#fff;font-size:15px;font-weight:500;letter-spacing:0.04em;',
      '  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;',
      '  text-shadow:0 1px 4px rgba(0,0,0,0.4);',
      '}'
    ].join('');
    document.head.appendChild(style);

    var overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;

    var spinner = document.createElement('div');
    spinner.id = OVERLAY_ID + '__spinner';

    var label = document.createElement('div');
    label.id = OVERLAY_ID + '__label';
    label.textContent = 'Adding to cart\u2026';

    overlay.appendChild(spinner);
    overlay.appendChild(label);
    document.body.appendChild(overlay);

    // Trigger fade-in on next frame
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add('visible');
      });
    });
  }

  function removeAddingOverlay() {
    var overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return;
    overlay.classList.remove('visible');
    // Remove after fade-out transition
    setTimeout(function () {
      overlay.parentNode && overlay.parentNode.removeChild(overlay);
      var style = document.getElementById(OVERLAY_ID + '_style');
      style && style.parentNode && style.parentNode.removeChild(style);
    }, 280);
  }

  /* ─── Personalize popup selectors ────────────────────────────────────── */

  var DEFAULT_SELECTORS = [
    '#pplr_modal',
    '[id^="pplr_modal"]',
    '[id^="pplr-modal"]',
    '.pplr-modal',
    '.pplr-modal-wrap',
    '.pplr_crop-modal',
    '.pplr-modal-overlay',
    '.pplr_overlay',
    '#pplr_overlay',
    '.pplr_popup',
    '.pplr-popup',
    '.pplr-modal-box',
    '.pplr-customizer',
    '.pplr_customizer'
  ];

  function cleanupOverlays() {
    DEFAULT_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        try {
          el.remove();
        } catch (_) {
          el.style.display = 'none';
        }
      });
    });

    document.body.classList.remove('pplr-modal-open', 'pplr_open', 'pplr_fixed');
  }

  var FEEDBACK_POLL_MS = 50;
  var FEEDBACK_MAX_WAIT_MS = 8000;

  function runClosePersonalizer() {
    try {
      if (typeof window.pplr_w_h === 'function') window.pplr_w_h();
    } catch (_) {}

    cleanupOverlays();
    setTimeout(cleanupOverlays, 60);
    setTimeout(cleanupOverlays, 520);
    setTimeout(removeAddingOverlay, 600);
  }

  function isCartFeedbackVisible() {
    var qc = document.querySelector('.quick-cart__wrapper');
    if (qc && qc.classList.contains('active')) return true;
    var pc = document.querySelector('.purchase-confirmation-popup');
    return !!(pc && pc.classList.contains('active'));
  }

  /** After add-to-cart succeeds: wait for theme feedback, then close. */
  function scheduleClosePersonalizerAfterCartFeedback() {

    var settled = false;
    var pollId;

    function settle() {
      if (settled) return;
      settled = true;
      if (pollId) clearInterval(pollId);
      document.removeEventListener('quick-cart:open', onQuickCartOpen);
      runClosePersonalizer();
    }

    function onQuickCartOpen() {
      settle();
    }

    document.addEventListener('quick-cart:open', onQuickCartOpen, { once: true });

    var deadline = Date.now() + FEEDBACK_MAX_WAIT_MS;

    setTimeout(function afterThemeMicrotasks() {
      if (settled) return;
      if (isCartFeedbackVisible()) settle();
      else {
        pollId = setInterval(function () {
          if (isCartFeedbackVisible()) settle();
          else if (Date.now() > deadline) settle();
        }, FEEDBACK_POLL_MS);
      }
    }, 0);
  }

  function closePersonalizerPopup() {
    scheduleClosePersonalizerAfterCartFeedback();
  }

  function isCartAddUrl(url) {
    return !!url && /\/cart\/add(\.js|\.json)?(\?|$)/i.test(String(url));
  }

  function installPdpPersonalizeAutoClose() {
    if (window.__pdpPersonalizeAutoCloseInstalled) return;
    window.__pdpPersonalizeAutoCloseInstalled = true;

    var originalFetch = window.fetch;
    if (typeof originalFetch === 'function') {
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = originalFetch.apply(this, arguments);
        if (!isCartAddUrl(url)) return promise;
        // Show overlay the instant the /cart/add request is dispatched
        showAddingOverlay();
        return promise.then(function (res) {
          if (res && res.ok) {
            scheduleClosePersonalizerAfterCartFeedback();
          } else {
            // Cart add failed — dismiss overlay so the user can try again
            removeAddingOverlay();
          }
          return res;
        }, function (err) {
          removeAddingOverlay();
          throw err;
        });
      };
    }

    var Xhr = window.XMLHttpRequest;
    if (Xhr && Xhr.prototype && Xhr.prototype.open && Xhr.prototype.send) {
      var originalOpen = Xhr.prototype.open;
      var originalSend = Xhr.prototype.send;

      Xhr.prototype.open = function (method, url) {
        this.__pdpCartAddUrl = url;
        return originalOpen.apply(this, arguments);
      };

      Xhr.prototype.send = function () {
        if (isCartAddUrl(this.__pdpCartAddUrl)) {
          // Show overlay the instant the /cart/add request is sent
          showAddingOverlay();
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) {
              scheduleClosePersonalizerAfterCartFeedback();
            } else {
              removeAddingOverlay();
            }
          });
          this.addEventListener('error', function () {
            removeAddingOverlay();
          });
        }
        return originalSend.apply(this, arguments);
      };
    }
  }

  window.closePersonalizerPopup = closePersonalizerPopup;
  window.installPdpPersonalizeAutoClose = installPdpPersonalizeAutoClose;

  // Auto-install on load for convenience.
  installPdpPersonalizeAutoClose();
})();
